import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageCircleQuestion, Send, X, Minus, Bot, ExternalLink } from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';
import useFirmInfo from '../../hooks/useFirmInfo';

// Per-host ESCALATION routing only (whether/where to escalate). The firm's
// display NAME comes from useFirmInfo (firm_settings), never from this map — see
// the naming rule in useFirmInfo.js. `firm` here is only a last-resort fallback.
const FIRM_ESCALATION = {
  'virgolaw.app':          { firm: 'your firm', escalate: true,  channel: 'planchette' },
  // Removed: astraealaw.com entry
};

const firmConfig = FIRM_ESCALATION[window.location.hostname] || {
  firm: 'your firm',
  escalate: false,
  channel: null,
};

const PortalHelpWidget = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { firmName } = useFirmInfo();
  const firmDisplayName = firmName || firmConfig.firm;  // firm_settings first; fallback last
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const isAuthenticated = !!localStorage.getItem('client_portal_token');
  const isLoginPage = location.pathname === '/login' || location.pathname === '/portal/login';
  const isPreAuthPage = ['/login', '/portal/login', '/portal/forgot-password', '/portal/reset-password'].includes(location.pathname);
  const isPortalPage = location.pathname.startsWith('/portal') && location.pathname !== '/portal/ai';

  const shouldHide = !isLoginPage && !isPortalPage;

  const getSessionId = () => {
    let sid = sessionStorage.getItem('portal_help_session_id');
    if (!sid) {
      sid = crypto.randomUUID();
      sessionStorage.setItem('portal_help_session_id', sid);
    }
    return sid;
  };

  // Persist open/closed state
  useEffect(() => {
    const saved = sessionStorage.getItem('portal_help_open');
    if (saved === 'true') setIsOpen(true);
  }, []);

  useEffect(() => {
    sessionStorage.setItem('portal_help_open', isOpen ? 'true' : 'false');
  }, [isOpen]);

  // Auto-open on login page after 30s (first visit nudge)
  useEffect(() => {
    if (!isLoginPage) return;
    const attempts = parseInt(sessionStorage.getItem('portal_login_attempts') || '0', 10);
    // Increment login page visit counter
    sessionStorage.setItem('portal_login_attempts', String(attempts + 1));

    if (attempts >= 2) {
      // Returning visitor having trouble — open immediately
      if (!isOpen && messages.length === 0) {
        openWithMessage("Welcome back. Still having trouble getting in? Let me help.");
      }
      return;
    }

    const hasVisited = sessionStorage.getItem('portal_help_nudged');
    if (hasVisited || isOpen) return;

    const timer = setTimeout(() => {
      if (!isOpen) {
        setHasUnread(true);
        sessionStorage.setItem('portal_help_nudged', 'true');
      }
    }, 30000);
    return () => clearTimeout(timer);
  }, [isLoginPage]);

  // Welcome message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMsg = isLoginPage
        ? `Hi! I'm here to help you sign in to ${firmDisplayName}'s client portal. Having trouble logging in? Tell me what's happening and I'll get you sorted out.`
        : isAuthenticated
        ? "Hi! Need help navigating the portal? I can also answer questions about your case."
        : "Hi! I'm Astraea. I can help you access the portal. What do you need help with?";
      setMessages([{ role: 'assistant', content: welcomeMsg }]);
    }
  }, [isOpen]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ESC to close
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape' && isOpen) setIsOpen(false); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen]);

  // Listen for login failures
  useEffect(() => {
    const handleLoginFailed = (e) => {
      if (isOpen && messages.length > 1) return; // Already helping

      const type = e.detail?.type || 'password';
      let proactiveMsg = '';
      let quickActions = [];

      if (type === 'password') {
        proactiveMsg = "It looks like you're having trouble signing in. I can help \u2014 what's going on? Did you forget your password, or is something else happening?";
        quickActions = [
          { label: 'Reset my password', action: 'navigate', target: '/portal/forgot-password' },
          { label: 'I never set a password', action: 'send', message: 'I never set a password for the portal' },
          { label: 'Call the firm', action: 'tel' },
        ];
      } else if (type === 'otp') {
        proactiveMsg = "Looks like the 6-digit code didn't work. Check your spam folder, or wait a moment and use the Resend Code button. Still not getting it? I can help.";
        quickActions = [
          { label: "I'm not getting the code", action: 'send', message: "I'm not receiving the 2FA verification code" },
          { label: 'Try a different email', action: 'send', message: 'Can I use a different email address for verification?' },
          { label: 'Call the firm', action: 'tel' },
        ];
      } else if (type === 'locked') {
        proactiveMsg = "Your account has been temporarily locked after too many attempts. It'll unlock automatically in 15 minutes. If you need immediate access, call us at your firm.";
        quickActions = [
          { label: 'Call the firm', action: 'tel' },
        ];
      }

      if (proactiveMsg) {
        setMessages(prev => {
          if (prev.length === 0) {
            return [{ role: 'assistant', content: proactiveMsg, quickActions }];
          }
          return [...prev, { role: 'assistant', content: proactiveMsg, quickActions }];
        });
        setIsOpen(true);
        setHasUnread(false);
      }
    };

    const handleLoginSuccess = () => {
      sessionStorage.removeItem('portal_login_attempts');
    };

    window.addEventListener('portal-login-failed', handleLoginFailed);
    window.addEventListener('portal-login-success', handleLoginSuccess);
    return () => {
      window.removeEventListener('portal-login-failed', handleLoginFailed);
      window.removeEventListener('portal-login-success', handleLoginSuccess);
    };
  }, [isOpen, messages.length]);

  const openWithMessage = (msg) => {
    setMessages([{ role: 'assistant', content: msg }]);
    setIsOpen(true);
    setHasUnread(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
    setHasUnread(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = text.trim();
    setInput('');
    setCharCount(0);
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      let aiResponse;
      if (isAuthenticated && !isPreAuthPage) {
        const resp = await clientPortalService.chatWithAI(userMsg);
        aiResponse = resp.response;
      } else {
        const resp = await fetch('/api/client-portal/help/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: userMsg,
            session_id: getSessionId(),
            firm_name: firmDisplayName,
            escalate: firmConfig.escalate,
            escalate_channel: firmConfig.channel,
            context: isLoginPage ? 'login_help' : 'portal_help',
          })
        });
        if (!resp.ok) throw new Error('Help request failed');
        const data = await resp.json();
        aiResponse = data.response;
      }
      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Something went wrong. Call us at your firm or email your firm.",
        error: true
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleQuickAction = (action) => {
    if (action.action === 'navigate') {
      navigate(action.target);
    } else if (action.action === 'send') {
      sendMessage(action.message);
    } else if (action.action === 'tel') {
      window.open('#', '_self');
    }
  };

  const handleSend = () => sendMessage(input);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const preAuthQuickActions = [
    { label: 'I forgot my password', action: 'navigate', target: '/portal/forgot-password' },
    { label: "I didn't get my login email", action: 'send', message: "I never received my portal invitation email" },
    { label: "I'm not getting the 6-digit code", action: 'send', message: "I'm not receiving the 2FA verification code email" },
    { label: "I can't log in", action: 'send', message: "I'm having trouble logging into the portal" },
  ];

  const postAuthQuickActions = [
    { label: "What's my case status?", action: 'send', message: "What's my case status?" },
    { label: 'Any upcoming deadlines?', action: 'send', message: "Do I have any upcoming deadlines?" },
    { label: 'How do I upload a document?', action: 'send', message: "How do I upload a document?" },
    { label: 'How do I pay my invoice?', action: 'send', message: "How do I pay my invoice?" },
  ];

  const defaultQuickActions = isAuthenticated && !isPreAuthPage ? postAuthQuickActions : preAuthQuickActions;

  if (shouldHide) return null;

  // Collapsed button
  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={handleOpen}
          className="group relative w-14 h-14 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 hover:scale-105 transition-all flex items-center justify-center"
          title="Need help?"
        >
          <MessageCircleQuestion size={24} />
          {hasUnread && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse" />
          )}
          <span className="absolute right-full mr-3 px-3 py-1 bg-virgo-navy-700 border border-virgo-teal-700/50 text-virgo-teal-200 text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Need help?
          </span>
        </button>
      </div>
    );
  }

  // Expanded panel — mobile: full screen, desktop: floating
  return (
    <div className="fixed bottom-6 right-6 z-50 sm:w-80 w-full sm:h-[420px] h-full sm:bottom-6 sm:right-6 bottom-0 right-0 flex flex-col bg-gradient-to-br from-virgo-navy-800 to-virgo-navy-900 sm:rounded-xl rounded-none border border-virgo-teal-700/30 shadow-2xl shadow-black/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-virgo-navy-700/80 border-b border-virgo-teal-700/30 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <img src="/astraea-avatar.png" alt="Astraea" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
          <div>
            <p className="text-white text-sm font-semibold leading-tight">Astraea</p>
            <p className="text-virgo-teal-500 text-xs leading-tight">Portal Assistant</p>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          {isAuthenticated && !isPreAuthPage && (
            <button
              onClick={() => navigate('/portal/ai')}
              className="p-1.5 text-virgo-teal-400 hover:text-cyan-400 transition-colors"
              title="Full Assistant"
            >
              <ExternalLink size={14} />
            </button>
          )}
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 text-virgo-teal-400 hover:text-white transition-colors"
            title="Minimize"
          >
            <Minus size={14} />
          </button>
          <button
            onClick={() => { setIsOpen(false); setMessages([]); }}
            className="p-1.5 text-virgo-teal-400 hover:text-red-400 transition-colors"
            title="Close"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {messages.map((msg, i) => (
          <div key={i}>
            <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                    : msg.error
                    ? 'bg-red-500/20 border border-red-500/30 text-red-300'
                    : 'bg-virgo-navy-700 border border-virgo-teal-700/30 text-virgo-teal-100'
                }`}
              >
                {msg.role === 'assistant' && (
                  <img src="/astraea-avatar.png" alt="" className="w-4 h-4 rounded-full object-cover inline mr-1 -mt-0.5" />
                )}
                <span className="whitespace-pre-wrap">{msg.content}</span>
              </div>
            </div>
            {/* Quick actions attached to this message */}
            {msg.quickActions && (
              <div className="flex flex-wrap gap-1.5 mt-2 ml-1">
                {msg.quickActions.map((qa, j) => (
                  <button
                    key={j}
                    onClick={() => handleQuickAction(qa)}
                    className="px-2.5 py-1 text-xs bg-virgo-navy-700 border border-virgo-teal-700/50 rounded-full text-virgo-teal-300 hover:border-cyan-500/50 hover:text-cyan-300 transition-colors"
                  >
                    {qa.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-virgo-navy-700 border border-virgo-teal-700/30 rounded-xl px-3 py-2">
              <div className="flex items-center space-x-1.5">
                <img src="/astraea-avatar.png" alt="" className="w-4 h-4 rounded-full object-cover" />
                <span className="flex space-x-1">
                  <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Default quick actions (shown when conversation is fresh) */}
      {messages.length <= 1 && !loading && (
        <div className="px-3 pb-2 flex flex-wrap gap-1.5">
          {defaultQuickActions.map((qa, i) => (
            <button
              key={i}
              onClick={() => handleQuickAction(qa)}
              className="px-2.5 py-1 text-xs bg-virgo-navy-700 border border-virgo-teal-700/50 rounded-full text-virgo-teal-300 hover:border-cyan-500/50 hover:text-cyan-300 transition-colors"
            >
              {qa.label}
            </button>
          ))}
          {isLoginPage && firmConfig.escalate && (
            <button
              onClick={() => sendMessage("I need to speak with someone at the firm to get access.")}
              className="px-3 py-1.5 bg-virgo-teal-600/20 border border-virgo-teal-500/30 text-virgo-teal-300 text-xs rounded-lg hover:bg-virgo-teal-600/30 transition-colors"
            >
              Contact {firmConfig.firm}
            </button>
          )}
        </div>
      )}

      {/* Input */}
      <div className="px-3 py-2 border-t border-virgo-teal-700/30 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => { setInput(e.target.value.slice(0, 500)); setCharCount(e.target.value.length); }}
            onKeyDown={handleKeyPress}
            placeholder="Type your question..."
            maxLength={500}
            className="flex-1 px-3 py-2 bg-virgo-navy-700 border border-virgo-teal-700/50 rounded-lg text-white text-sm placeholder-virgo-teal-500 focus:ring-1 focus:ring-cyan-500 focus:border-transparent"
          />
          <button
            onClick={handleSend}
            disabled={loading || !input.trim()}
            className="p-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all flex-shrink-0"
          >
            <Send size={16} />
          </button>
        </div>
        <div className="flex items-center justify-between mt-1">
          {charCount > 400 && (
            <span className="text-xs text-virgo-teal-500">{charCount}/500</span>
          )}
          <span className="text-xs text-virgo-teal-600 ml-auto">Powered by Astraea AI</span>
        </div>
      </div>
    </div>
  );
};

export default PortalHelpWidget;
